import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, MapPin } from 'lucide-react';
import { MainLayout } from '@/components/layout';
import { Button, Card } from '@/components/common';
import MapPicker from '@/components/map/MapPicker';
import { ThaiAddressSelector } from '@/components/address';
import { toast } from 'react-toastify';
import { API_BASE_URL, API_ENDPOINTS } from '@/constants';
import { storageService } from '@/services';

interface FarmFormData {
  name: string;
  farmCode: string;
  description: string;
  landArea: string;
  address: string;
  subdistrict: string;
  district: string;
  province: string;
  postalCode: string;
  latitude: string;
  longitude: string;
  status: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
}

export default function FarmFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<FarmFormData>({
    name: '',
    farmCode: '',
    description: '',
    landArea: '',
    address: '',
    subdistrict: '',
    district: '',
    province: '',
    postalCode: '',
    latitude: '',
    longitude: '',
    status: 'ACTIVE',
  });

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEditMode);

  useEffect(() => {
    if (isEditMode) {
      fetchFarmData();
    }
  }, [id]);

  const fetchFarmData = async () => {
    try {
      const token = storageService.getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.FARMS.DETAIL(id!)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch farm data');

      const data = await response.json();
      setFormData({
        name: data.name || '',
        farmCode: data.farmCode || '',
        description: data.description || '',
        landArea: data.landArea?.toString() || '',
        address: data.address || '',
        subdistrict: data.subdistrict || '',
        district: data.district || '',
        province: data.province || '',
        postalCode: data.postalCode || '',
        latitude: data.latitude?.toString() || '',
        longitude: data.longitude?.toString() || '',
        status: data.status || 'ACTIVE',
      });
    } catch (error) {
      console.error('Error fetching farm:', error);
      toast.error('ไม่สามารถโหลดข้อมูลฟาร์มได้');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLocationSelect = (lat: number, lng: number) => {
    setFormData((prev) => ({
      ...prev,
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6),
    }));
  };

  const parseErrorMessage = (error: any): string => {
    const message = error.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล';

    // แปลง error message ให้อ่านง่ายขึ้น
    if (message.includes('Farm code') && message.includes('already exists')) {
      return '❌ รหัสฟาร์มนี้มีในระบบแล้ว กรุณาใช้รหัสอื่น';
    }

    if (message.includes('duplicate') || message.includes('already exists')) {
      return '❌ ข้อมูลซ้ำกับในระบบ กรุณาตรวจสอบข้อมูล';
    }

    if (message.includes('required')) {
      return '❌ กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน';
    }

    if (message.includes('invalid')) {
      return '❌ ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบข้อมูลอีกครั้ง';
    }

    // กรณีอื่นๆ ให้แสดง message เดิมพร้อมไอคอน error
    return `❌ ${message}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = storageService.getAuthToken();
      const url = isEditMode
        ? `${API_BASE_URL}${API_ENDPOINTS.FARMS.UPDATE(id!)}`
        : `${API_BASE_URL}${API_ENDPOINTS.FARMS.CREATE}`;

      const method = isEditMode ? 'PUT' : 'POST';

      const payload = {
        ...formData,
        landArea: parseFloat(formData.landArea) || 0,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save farm');
      }

      const result = await response.json();
      toast.success(
        isEditMode ? '✅ แก้ไขฟาร์มสำเร็จ' : '✅ เพิ่มฟาร์มใหม่สำเร็จ',
        { autoClose: 2000 }
      );
      navigate(`/farms/${result.id}`);
    } catch (error: any) {
      console.error('Error saving farm:', error);
      const errorMessage = parseErrorMessage(error);
      toast.error(errorMessage, {
        autoClose: 5000,
        style: {
          whiteSpace: 'pre-line',
          fontSize: '14px',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-800"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="secondary"
            onClick={() => navigate(isEditMode ? `/farms/${id}` : '/farms')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            กลับ
          </Button>
          <h1 className="text-3xl font-bold text-text-primary">
            {isEditMode ? 'แก้ไขฟาร์ม' : 'เพิ่มฟาร์มใหม่'}
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Card>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* ชื่อฟาร์ม */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ชื่อฟาร์ม <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="ฟาร์มโคนมสุขใจ"
                />
              </div>

              {/* รหัสฟาร์ม */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  รหัสฟาร์ม <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="farmCode"
                  value={formData.farmCode}
                  onChange={handleChange}
                  required
                  disabled={isEditMode}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                  placeholder="FARM001"
                />
              </div>

              {/* พื้นที่ */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  พื้นที่ (ไร่) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="landArea"
                  value={formData.landArea}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="10.5"
                />
              </div>

              {/* สถานะ */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  สถานะ
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="ACTIVE">ใช้งาน</option>
                  <option value="INACTIVE">ไม่ใช้งาน</option>
                  <option value="ARCHIVED">เก็บถาวร</option>
                </select>
              </div>

              {/* คำอธิบาย */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  คำอธิบาย
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="รายละเอียดเพิ่มเติมเกี่ยวกับฟาร์ม..."
                />
              </div>

              {/* ที่อยู่ */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ที่อยู่ <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="123 หมู่ 5"
                />
              </div>

              {/* Thai Address Selector - จังหวัด, อำเภอ, ตำบล, รหัสไปรษณีย์ */}
              <ThaiAddressSelector
                province={formData.province}
                district={formData.district}
                subdistrict={formData.subdistrict}
                postalCode={formData.postalCode}
                onProvinceChange={(value) => setFormData((prev) => ({ ...prev, province: value }))}
                onDistrictChange={(value) => setFormData((prev) => ({ ...prev, district: value }))}
                onSubdistrictChange={(value) => setFormData((prev) => ({ ...prev, subdistrict: value }))}
                onPostalCodeChange={(value) => setFormData((prev) => ({ ...prev, postalCode: value }))}
                required
              />

              {/* Latitude */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ละติจูด (Latitude)
                </label>
                <input
                  type="number"
                  step="any"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="18.7883"
                />
              </div>

              {/* Longitude */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ลองจิจูด (Longitude)
                </label>
                <input
                  type="number"
                  step="any"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="98.9853"
                />
              </div>

              {/* Map Picker */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  เลือกตำแหน่งจากแผนที่
                </label>
                <MapPicker
                  latitude={formData.latitude ? parseFloat(formData.latitude) : undefined}
                  longitude={formData.longitude ? parseFloat(formData.longitude) : undefined}
                  onLocationSelect={handleLocationSelect}
                  className="h-96"
                />
                <p className="text-xs text-gray-500 mt-2">
                  คลิกบนแผนที่เพื่อเลือกตำแหน่งฟาร์ม ค่าพิกัดจะถูกอัพเดทอัตโนมัติ
                </p>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex items-center gap-4 mt-6 pt-6 border-t">
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
                className="flex items-center gap-2"
              >
                <Save className="w-5 h-5" />
                {loading ? 'กำลังบันทึก...' : 'บันทึก'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate(isEditMode ? `/farms/${id}` : '/farms')}
                disabled={loading}
              >
                ยกเลิก
              </Button>
            </div>
          </Card>
        </form>
      </div>
    </MainLayout>
  );
}
